import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer with WhatsApp checkout.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
    this.#addWhatsAppCheckout();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  // ---------------------------
  // WhatsApp Checkout Form
  // ---------------------------
  #addWhatsAppCheckout() {
    const { dialog } = this.refs;
    if (!dialog) return;

    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!summary) return;

    // Remove existing form if present
    const existing = summary.querySelector('.whatsapp-checkout-form');
    if (existing) existing.remove();

    const formDiv = document.createElement('div');
    formDiv.className = 'whatsapp-checkout-form';
    formDiv.innerHTML = `
      <form id="whatsapp-form">
        <div class="form-group">
          <label for="customer-naam">Naam *</label>
          <input type="text" id="customer-naam" name="naam" required>
        </div>
        
        <div class="form-group">
          <label for="customer-achternaam">Achternaam *</label>
          <input type="text" id="customer-achternaam" name="achternaam" required>
        </div>
        
        <div class="form-group">
          <label for="customer-email">Email *</label>
          <input type="email" id="customer-email" name="email" required>
        </div>
        
        <div class="form-group">
          <label for="customer-telefoon">Telefoonnummer *</label>
          <input type="tel" id="customer-telefoon" name="telefoon" required>
        </div>
        
        <div class="form-group">
          <label for="customer-adres">Adres *</label>
          <textarea id="customer-adres" name="adres" rows="3" required></textarea>
        </div>
        
        <button type="submit" class="whatsapp-submit-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          BESTELLING AFRONDEN
        </button>
      </form>
    `;

    summary.appendChild(formDiv);

    // Add form submit handler
    const form = formDiv.querySelector('#whatsapp-form');
    form.addEventListener('submit', (e) => this.#handleWhatsAppSubmit(e));
  }

  async #handleWhatsAppSubmit(e) {
    e.preventDefault();

    // Get form data
    const form = e.target;
    const customerInfo = {
      naam: form.querySelector('#customer-naam').value.trim(),
      achternaam: form.querySelector('#customer-achternaam').value.trim(),
      email: form.querySelector('#customer-email').value.trim(),
      telefoon: form.querySelector('#customer-telefoon').value.trim(),
      adres: form.querySelector('#customer-adres').value.trim()
    };

    // Show loading state briefly
    const submitBtn = form.querySelector('.whatsapp-submit-btn');
    submitBtn.classList.add('loading');

    try {
      // Get cart items
      const cartItems = await this.#getCartItems();

      // Generate WhatsApp message
      const message = this.#generateWhatsAppMessage(customerInfo, cartItems);

      // WhatsApp number (replace with actual number)
      const phoneNumber = '31684488616'; // Format: country code + number without +

      // Create WhatsApp URL
      const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

      // Open WhatsApp
      window.open(whatsappURL, '_blank');

      // Remove loading state (form data will remain)
      setTimeout(() => {
        submitBtn.classList.remove('loading');
      }, 1000);
    } catch (error) {
      console.error('Error submitting WhatsApp form:', error);
      alert('Er is een fout opgetreden. Probeer het opnieuw.');
      submitBtn.classList.remove('loading');
    }
  }

  async #getCartItems() {
    // METHOD 1: Try Shopify Cart API first (MOST RELIABLE)
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (cart.items && cart.items.length > 0) {
        return cart.items.map(item => ({
          title: item.product_title,
          price: `€${(item.final_line_price / 100).toFixed(2)}`,
          quantity: item.quantity,
          link: item.url ? window.location.origin + item.url : ''
        }));
      }
    } catch (error) {
      console.error('Error fetching cart from API:', error);
    }

    // METHOD 2: Try theme cart object
    if (window.theme?.cart?.items && window.theme.cart.items.length > 0) {
      return window.theme.cart.items.map(item => ({
        title: item.product_title || item.title,
        price: item.price ? `€${(item.price / 100).toFixed(2)}` : '',
        quantity: item.quantity || 1,
        link: item.url ? window.location.origin + item.url : ''
      }));
    }

    // METHOD 3: Parse from DOM as fallback
    const items = [];
    const cartItemElements = document.querySelectorAll('.cart-item, [data-cart-item], .cart-drawer__item');

    cartItemElements.forEach(item => {
      const title = item.querySelector('.cart-item__title, [data-cart-item-title], .cart-item__name')?.textContent?.trim() || '';
      const priceElement = item.querySelector('.cart-item__price, [data-cart-item-price], .cart-item__price-wrapper .price');
      const price = priceElement?.textContent?.trim() || '';
      const quantityInput = item.querySelector('.cart-item__quantity input, [data-cart-item-quantity], input[name*="quantity"]');
      const quantity = quantityInput?.value || '1';
      
      // Get product link
      const linkElement = item.querySelector('.cart-item__title a, [data-cart-item-title] a, a[href*="/products/"]');
      const link = linkElement ? window.location.origin + linkElement.getAttribute('href') : '';

      if (title) {
        items.push({ title, price, quantity, link });
      }
    });

    return items;
  }

  #generateWhatsAppMessage(customerInfo, cartItems) {
    let message = '🛒 *NIEUWE BESTELLING*\n\n';

    // Customer information - FORM DATA
    message += '👤 *KLANTGEGEVENS:*\n';
    message += `Naam: ${customerInfo.naam} ${customerInfo.achternaam}\n`;
    message += `Email: ${customerInfo.email}\n`;
    message += `Telefoon: ${customerInfo.telefoon}\n`;
    message += `Adres: ${customerInfo.adres}\n\n`;

    // Cart items - PRODUCT DATA WITH LINKS
    message += '📦 *BESTELDE PRODUCTEN:*\n';
    if (cartItems.length > 0) {
      cartItems.forEach((item, index) => {
        message += `${index + 1}. ${item.title}\n`;
        message += `   Aantal: ${item.quantity}\n`;
        message += `   Prijs: ${item.price}\n`;
        if (item.link) {
          message += `   Link: ${item.link}\n`;
        }
        message += '\n';
      });
    } else {
      message += 'Geen producten gevonden in winkelwagen\n\n';
    }

    // Total
    const totalElement = document.querySelector('.cart-drawer__total, [data-cart-total], .cart__footer-total');
    const total = totalElement?.textContent?.trim() || '';
    if (total) {
      message += `💰 *TOTAAL: ${total}*`;
    }

    return message;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}